import React, { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { getEquivalentRoute } from '../lib/i18n'

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [openDropdown, setOpenDropdown] = useState(null)
  const [mobileExpanded, setMobileExpanded] = useState({})

  const location = useLocation()
  const navigate = useNavigate()

  const isEn = location.pathname.startsWith('/en')

  const toggleMobileSubmenu = (key) => {
    setMobileExpanded((prev) => ({
      ...prev,
      [key]: !prev[key]
    }))
  }

  const handleLanguageSwitch = (targetLang) => {
    localStorage.setItem('preferred_lang', targetLang)
    const targetPath = getEquivalentRoute(location.pathname, targetLang)
    navigate(targetPath)
  }

  // Danish Navigation items
  const navItemsDa = [
    { label: 'Lovpakke & Priser', path: '/lovpakke-priser' },
    {
      label: 'Teori & Undervisning',
      path: '/teori-undervisning',
      children: [
        { label: 'Teori & Undervisning', path: '/teori-undervisning' },
        { label: 'Opstart', path: '/opstart' },
        { label: 'Valg af køreskole', path: '/valg-af-koereskole' }
      ]
    },
    {
      label: 'Om os',
      path: '/om-os',
      children: [
        { label: 'Om Borgs Køreskole', path: '/om-os' },
        { label: 'Betingelser', path: '/betingelser' }
      ]
    },
    { label: 'Anbefalinger', path: '/anbefalinger' },
    { label: 'Links', path: '/links' },
    { label: 'Kontakt', path: '/kontakt' }
  ]

  // English Navigation items
  const navItemsEn = [
    { label: 'Mandatory Package & Prices', path: '/en/mandatory-package-prices' },
    {
      label: 'Theory & Lessons',
      path: '/en/theory-classes',
      children: [
        { label: 'Theory & Lessons', path: '/en/theory-classes' },
        { label: 'Getting Started', path: '/en/start-dates' },
        { label: 'Choosing a Driving School', path: '/en/choosing-a-driving-school' }
      ]
    },
    {
      label: 'About Us',
      path: '/en/about-us',
      children: [
        { label: 'About Borgs Driving School', path: '/en/about-us' },
        { label: 'Terms & Conditions', path: '/en/terms' }
      ]
    },
    { label: 'Testimonials', path: '/en/testimonials' },
    { label: 'Links', path: '/en/links' },
    { label: 'Contact', path: '/en/contact' }
  ]

  const navItems = isEn ? navItemsEn : navItemsDa

  const isActive = (item) => {
    if (item.isExternal) return false
    if (item.path === '/' || item.path === '/en') {
      return location.pathname === item.path
    }
    if (item.children) {
      return item.children.some((child) => location.pathname === child.path)
    }
    return location.pathname.startsWith(item.path)
  }

  const daTarget = getEquivalentRoute(location.pathname, 'da')
  const enTarget = getEquivalentRoute(location.pathname, 'en')

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-emerald-100 shadow-sm transition-all duration-200">
      {/* Top micro bar for phone & address & Language Switcher */}
      <div className="hidden sm:block bg-emerald-900 text-white text-xs py-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-wrap justify-between items-center gap-2">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2 text-white">
              <i className="fa-solid fa-phone text-white" aria-hidden="true"></i>
              <span>
                {isEn ? 'Call: ' : 'Ring: '}
                <a href="tel:21470187" className="hover:text-emerald-200 transition-colors font-bold text-white">21 47 01 87</a> / <a href="tel:23200425" className="hover:text-emerald-200 transition-colors font-bold text-white">23 20 04 25</a>
              </span>
            </div>
            <span className="hidden sm:inline text-emerald-400/60">|</span>
            <a 
              href="mailto:info@borgskoereskole.dk" 
              className="hidden sm:flex items-center gap-2 hover:text-emerald-200 transition-colors whitespace-nowrap text-white"
            >
              <i className="fa-solid fa-envelope text-white" aria-hidden="true"></i>
              <span className="whitespace-nowrap text-white">info@borgskoereskole.dk</span>
            </a>
          </div>
          
          <div className="flex items-center gap-4 text-white">
            <span className="flex items-center gap-1.5 text-white">
              <i className="fa-solid fa-location-dot text-white" aria-hidden="true"></i>
              Elmegade 19, 4400 Kalundborg
            </span>

            <span className="text-emerald-400/60">|</span>

            {/* Language switcher in top bar */}
            <div className="inline-flex items-center p-0.5 rounded-full bg-emerald-950/70 border border-emerald-700/60 shadow-inner">
              <button
                type="button"
                onClick={() => handleLanguageSwitch('da')}
                className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-bold tracking-wide transition-all duration-200 cursor-pointer ${
                  !isEn 
                    ? 'bg-white text-slate-900 shadow-xs' 
                    : 'text-emerald-100/80 hover:text-white hover:bg-white/10'
                }`}
                title="Dansk"
                aria-label="Skift til dansk sprog"
              >
                <span className="text-[12px] leading-none select-none">🇩🇰</span>
                <span>DA</span>
              </button>
              <button
                type="button"
                onClick={() => handleLanguageSwitch('en')}
                className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-bold tracking-wide transition-all duration-200 cursor-pointer ${
                  isEn 
                    ? 'bg-white text-slate-900 shadow-xs' 
                    : 'text-emerald-100/80 hover:text-white hover:bg-white/10'
                }`}
                title="English"
                aria-label="Switch to English language"
              >
                <span className="text-[12px] leading-none select-none">🇬🇧</span>
                <span>EN</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Header Bar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Top Header Row: Logo & Actions */}
        <div className="min-h-[5.5rem] py-2 flex items-center justify-between border-b border-slate-100 min-[1200px]:border-slate-100/70">
          {/* Logo */}
          <Link to={isEn ? '/en' : '/'} className="flex items-center gap-2 group">
            <img 
              src="https://assets.auxodev.dk/cdn-cgi/image/width=400,quality=90,format=auto/assets/44bca6c4-b08c-481d-a28a-ebff52067c25/imgi-3-logo-18.png" 
              alt="Borgs Køreskole logo" 
              className="h-14 sm:h-16 lg:h-18 w-auto object-contain"
            />
          </Link>

          {/* Top Actions: Phone & Primary CTA on Desktop */}
          <div className="hidden min-[1200px]:flex items-center gap-4">
            <div className="flex items-center gap-2 text-slate-800 font-bold text-sm px-4 py-2.5 rounded-full bg-slate-50 border border-slate-300">
              <i className="fa-solid fa-phone text-emerald-600" aria-hidden="true"></i>
              <a href="tel:21470187" className="hover:text-emerald-600 transition-colors whitespace-nowrap">21 47 01 87</a>
              <span className="text-slate-300">/</span>
              <a href="tel:23200425" className="hover:text-emerald-600 transition-colors whitespace-nowrap">23 20 04 25</a>
            </div>

            <Link
              to={isEn ? '/en/mandatory-package-prices' : '/lovpakke-priser'}
              className="inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-full bg-[#8cc73d] hover:bg-[#7eb832] text-slate-950 font-bold text-sm transition-all duration-150 active:scale-[0.98] whitespace-nowrap"
            >
              <span>{isEn ? 'Register now' : 'Tilmeld dig nu'}</span>
              <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true"></i>
            </Link>
          </div>

          {/* Burger menu toggle & mobile language button (Visible only under 1200px) */}
          <div className="flex min-[1200px]:hidden items-center gap-2">
            {/* Quick mobile language toggle button */}
            <button
              type="button"
              onClick={() => handleLanguageSwitch(isEn ? 'da' : 'en')}
              className="px-2.5 py-1 rounded-full border border-slate-200 bg-white hover:bg-slate-50 text-xs font-bold text-slate-800 flex items-center gap-1.5 shadow-xs transition-colors"
              aria-label={isEn ? 'Skift til dansk' : 'Switch to English'}
            >
              <span>{isEn ? '🇩🇰' : '🇬🇧'}</span>
              <span>{isEn ? 'DA' : 'EN'}</span>
            </button>

            <a
              href="tel:21470187"
              className="p-2 text-emerald-700 hover:text-emerald-800 transition-colors whitespace-nowrap"
              aria-label="Ring til Borgs Køreskole"
            >
              <i className="fa-solid fa-phone text-base" aria-hidden="true"></i>
            </a>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 text-slate-700 hover:text-emerald-600 focus:outline-none"
              aria-label={isEn ? 'Open navigation' : 'Åbn navigation'}
            >
              <i className={`fa-solid ${mobileMenuOpen ? 'fa-xmark' : 'fa-bars'} text-xl`} aria-hidden="true"></i>
            </button>
          </div>
        </div>

        {/* Bottom Header Row: Menu Links positioned under logo (Desktop >= 1200px) */}
        <div className="hidden min-[1200px]:flex items-center justify-between py-2.5">
          <nav className="flex items-center gap-1.5">
            {navItems.map((item, idx) => {
              const active = isActive(item)
              const hasChildren = item.children && item.children.length > 0

              if (item.isExternal) {
                return (
                  <a
                    key={idx}
                    href={item.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-3 py-2 text-sm font-bold text-slate-700 hover:text-emerald-600 transition-colors inline-flex items-center gap-1"
                  >
                    <span>{item.label}</span>
                    <i className="fa-solid fa-arrow-up-right-from-square text-[10px] text-slate-400" aria-hidden="true"></i>
                  </a>
                )
              }

              if (!hasChildren) {
                return (
                  <Link
                    key={idx}
                    to={item.path}
                    className={`px-3 py-2 text-sm transition-all duration-150 relative ${
                      active
                        ? 'text-slate-900 font-extrabold'
                        : 'text-slate-700 font-bold hover:text-emerald-600'
                    }`}
                  >
                    {item.label}
                  </Link>
                )
              }

              return (
                <div
                  key={idx}
                  className="relative group"
                  onMouseEnter={() => setOpenDropdown(idx)}
                  onMouseLeave={() => setOpenDropdown(null)}
                >
                  <Link
                    to={item.path}
                    className={`px-3 py-2 text-sm transition-all duration-150 inline-flex items-center gap-1.5 ${
                      active
                        ? 'text-slate-900 font-extrabold'
                        : 'text-slate-700 font-bold hover:text-emerald-600'
                    }`}
                  >
                    <span>{item.label}</span>
                    <i className="fa-solid fa-chevron-down text-[10px] text-slate-400 group-hover:text-emerald-600 transition-transform duration-200 group-hover:rotate-180" aria-hidden="true"></i>
                  </Link>

                  {/* Dropdown Menu */}
                  <div className="absolute top-full left-0 pt-1 hidden group-hover:block w-56 z-50 animate-in fade-in slide-in-from-top-1 duration-150">
                    <div className="bg-white border border-slate-200 rounded-lg shadow-xl py-2">
                      {item.children.map((child, cIdx) => (
                        <Link
                          key={cIdx}
                          to={child.path}
                          className={`block px-4 py-2 text-xs font-semibold hover:bg-emerald-50 hover:text-emerald-800 transition-colors ${
                            location.pathname === child.path
                              ? 'text-emerald-700 font-bold bg-emerald-50/50'
                              : 'text-slate-700'
                          }`}
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              )
            })}
          </nav>

          <div className="text-xs font-semibold text-emerald-800">
            {isEn ? (
              <>Next class start: <strong>Register now</strong></>
            ) : (
              <>Næste holdstart: <strong>Tilmeld dig nu</strong></>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Drawer Navigation (< 1200px) */}
      {mobileMenuOpen && (
        <div className="min-[1200px]:hidden bg-white border-b border-slate-200 px-4 pt-3 pb-6 shadow-xl animate-in slide-in-from-top duration-200 max-h-[85vh] overflow-y-auto">
          {/* Language choice banner in mobile menu */}
          <div className="flex items-center justify-between bg-slate-100 p-3 rounded-xl mb-3">
            <span className="text-xs font-bold text-slate-700">
              {isEn ? 'Choose language:' : 'Vælg sprog:'}
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  setMobileMenuOpen(false)
                  handleLanguageSwitch('da')
                }}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1 ${
                  !isEn ? 'bg-emerald-700 text-white shadow-sm' : 'bg-white text-slate-700 border border-slate-200'
                }`}
              >
                <span>🇩🇰</span>
                <span>Dansk</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  setMobileMenuOpen(false)
                  handleLanguageSwitch('en')
                }}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1 ${
                  isEn ? 'bg-emerald-700 text-white shadow-sm' : 'bg-white text-slate-700 border border-slate-200'
                }`}
              >
                <span>🇬🇧</span>
                <span>English</span>
              </button>
            </div>
          </div>

          <div className="flex flex-col gap-1">
            {navItems.map((item, idx) => {
              const active = isActive(item)
              const hasChildren = item.children && item.children.length > 0
              const isExpanded = mobileExpanded[idx]

              if (item.isExternal) {
                return (
                  <a
                    key={idx}
                    href={item.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setMobileMenuOpen(false)}
                    className="px-4 py-3 text-base flex items-center justify-between border-b border-slate-100 text-slate-800 font-semibold hover:text-emerald-700"
                  >
                    <span>{item.label}</span>
                    <i className="fa-solid fa-arrow-up-right-from-square text-xs text-slate-400" aria-hidden="true"></i>
                  </a>
                )
              }

              if (!hasChildren) {
                return (
                  <Link
                    key={idx}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`px-4 py-3 text-base flex items-center justify-between border-b border-slate-100 ${
                      active
                        ? 'text-slate-900 font-extrabold bg-emerald-50/40'
                        : 'text-slate-800 font-semibold hover:text-emerald-700'
                    }`}
                  >
                    <span>{item.label}</span>
                    <i className={`fa-solid fa-chevron-right text-xs ${active ? 'text-slate-900' : 'text-slate-400'}`} aria-hidden="true"></i>
                  </Link>
                )
              }

              return (
                <div key={idx} className="border-b border-slate-100">
                  <div className="flex items-center justify-between px-4 py-3">
                    <Link
                      to={item.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`text-base font-semibold ${
                        active ? 'text-slate-900 font-extrabold' : 'text-slate-800 hover:text-emerald-700'
                      }`}
                    >
                      {item.label}
                    </Link>
                    <button
                      onClick={() => toggleMobileSubmenu(idx)}
                      className="p-2 text-slate-500 hover:text-emerald-700"
                      aria-label={`Åbn undermenu for ${item.label}`}
                    >
                      <i className={`fa-solid ${isExpanded ? 'fa-chevron-up' : 'fa-chevron-down'} text-xs`} aria-hidden="true"></i>
                    </button>
                  </div>

                  {isExpanded && (
                    <div className="bg-slate-50 px-6 py-2 space-y-2 border-t border-slate-100">
                      {item.children.map((child, cIdx) => (
                        <Link
                          key={cIdx}
                          to={child.path}
                          onClick={() => setMobileMenuOpen(false)}
                          className={`block py-1.5 text-sm ${
                            location.pathname === child.path
                              ? 'text-emerald-700 font-bold'
                              : 'text-slate-700 font-medium hover:text-emerald-700'
                          }`}
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              )
            })}
          </div>

          <div className="mt-5 pt-5 border-t border-slate-100 space-y-3">
            <div className="flex flex-col sm:flex-row gap-2">
              <a
                href="tel:21470187"
                className="w-full py-3 px-4 rounded-full bg-slate-100 text-slate-900 font-bold flex items-center justify-center gap-2 text-sm whitespace-nowrap hover:bg-emerald-50"
              >
                <i className="fa-solid fa-phone text-emerald-600" aria-hidden="true"></i>
                <span>{isEn ? 'Call: ' : 'Ring: '}21 47 01 87</span>
              </a>
              <a
                href="tel:23200425"
                className="w-full py-3 px-4 rounded-full bg-slate-100 text-slate-900 font-bold flex items-center justify-center gap-2 text-sm whitespace-nowrap hover:bg-emerald-50"
              >
                <i className="fa-solid fa-phone text-emerald-600" aria-hidden="true"></i>
                <span>{isEn ? 'Call: ' : 'Ring: '}23 20 04 25</span>
              </a>
            </div>
            <Link
              to={isEn ? '/en/contact' : '/kontakt'}
              onClick={() => setMobileMenuOpen(false)}
              className="w-full py-3.5 px-4 rounded-full bg-[#8cc73d] hover:bg-[#7eb832] text-slate-950 font-bold flex items-center justify-center gap-2 text-base whitespace-nowrap"
            >
              <span>{isEn ? 'Register now' : 'Tilmeld dig nu'}</span>
              <i className="fa-solid fa-arrow-right" aria-hidden="true"></i>
            </Link>
          </div>
        </div>
      )}
    </header>
  )
}
